import React from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { MODAL_KEYS } from '_constants/modals';
import { theme } from '_constants/theme';
import { selectModalDataByKey, selectModalVisibleByKey } from '_store/modals/selector';
import { hideModal } from '_store/modals/slice';
import { selectDeleting, selectStatementById } from '_store/statements/selector';
import { deleteStatement } from '_store/statements/slice';
import { selectCustomerId } from '_store/user/selector';

import Button from 'components/atomic/Button';
import Icon from 'components/atomic/Icon';
import Spinner from 'components/atomic/Spinner';
import Text from 'components/atomic/layouts/Text';

const StyledModalBody = styled(Modal.Body)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${theme.spaces.large2X}px;
`;

const StyledCloseActionContainer = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  top: ${-theme.spaces.large3X}px;
  right: 0;
  font-family: 'Inter';
  font-style: normal;
  font-size: ${theme.fontSizes.regular}px;
  font-weight: 400;
  color: ${theme.colors.white};
  cursor: pointer;
`;

const StyledIconContainer = styled.div`
  margin-right: ${theme.spaces.small2X}px;
`;

const StyledConfirmActionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spaces.smallX}px;
  margin-top: ${theme.spaces.small - 2}px;
`;

const StyledSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DeleteStatementModal = () => {
  const dispatch = useDispatch();
  const visible = useSelector(selectModalVisibleByKey(MODAL_KEYS.statementDelete));
  const { productId, statementId } = useSelector(selectModalDataByKey(MODAL_KEYS.statementDelete));
  const { description } = useSelector(selectStatementById(statementId)) || {};
  const deleting = useSelector(selectDeleting);
  const customerId = useSelector(selectCustomerId);

  const handleClose = () => {
    dispatch(hideModal({ key: MODAL_KEYS.statementDelete }));
  };

  const handleConfirm = () => {
    dispatch(deleteStatement({ customerId, productId, statementId }));
  };

  return (
    <Modal
      dialogClassName="statement-delete-confirmation-modal"
      size="sm"
      show={visible}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
    >
      <StyledModalBody>
        <StyledCloseActionContainer onClick={handleClose}>
          <StyledIconContainer>
            <Icon alias={ICON_ALIASES.close} color={theme.colors.white} />
          </StyledIconContainer>
          Close
        </StyledCloseActionContainer>
        <Text
          size={theme.fontSizes.largeX}
          weight={700}
          color={theme.colors.gray.primary}
          bottomSpace={theme.spaces.medium}
        >
          Are you sure?
        </Text>
        <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} bottomSpace={theme.spaces.medium}>
          You are about to delete a document: <b>{description}</b>
        </Text>
        <StyledConfirmActionContainer>
          <Button color={theme.colors.gray.secondary} disabled={deleting} onClick={handleClose}>
            Cancel
          </Button>
          <Button color={theme.colors.indigo.primary} disabled={deleting} onClick={handleConfirm}>
            Delete
          </Button>
          {deleting && (
            <StyledSpinnerContainer>
              <Spinner color={theme.colors.indigo.superLight} />
            </StyledSpinnerContainer>
          )}
        </StyledConfirmActionContainer>
      </StyledModalBody>
    </Modal>
  );
};

export default DeleteStatementModal;
